// Мастер подключения протокола (010: contracts/api.md, «Схемы»; research, Р3 – Р7, Р11). Ответ
// мастера — варианты шапки файла: по каждому лист-образец, строки шапки, колонки с подписью,
// значениями и ролью, поля схемы с уверенностью, предупреждения, вопросы администратору,
// похожая схема и строки листа-образца по ядру строения 1.7. Черновик не хранится.
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema } from '../kernel/common.ts';
import { DecisionStateSchema, HierarchyStateSchema } from './structure.ts';
import {
  ColumnAssignmentSchema,
  ColumnRoleOrUnusedSchema,
  ColumnRoleSchema,
  DecidedBySchema,
  RowKindCorrectionsSchema,
} from './template.ts';

/** Буква колонки листа: A … ZZZ. */
export const ColumnLetterSchema = z.string().regex(/^[A-Z]{1,3}$/);

/**
 * Поля схемы шаблона — ключи `columns` (003): работа — `text`. Обязательны номер, работа,
 * ответственный и срок (FR-012).
 */
export const templateFields = [
  'number',
  'text',
  'responsible',
  'due',
  'start',
  'contractor',
  'comment',
] as const;
export type TemplateField = (typeof templateFields)[number];
export const TemplateFieldSchema = z.enum(templateFields);
export const requiredTemplateFields = [
  'number',
  'text',
  'responsible',
  'due',
] as const satisfies readonly TemplateField[];

/** Роль колонки → поле схемы; остальные роли в схему не идут (FR-012). */
export const fieldOfRole = {
  number: 'number',
  work: 'text',
  responsible: 'responsible',
  due: 'due',
  start: 'start',
  contractor: 'contractor',
  comment: 'comment',
} as const satisfies Partial<Record<z.infer<typeof ColumnRoleSchema>, TemplateField>>;

/** Строк в шапке: 1 или 2 (FR-005, FR-032). */
export const HeaderRowsSchema = z.union([z.literal(1), z.literal(2)]);

/** Вид листа-образца: подписи колонок и до 20 строк ниже шапки, как в файле. */
export const SheetGridSchema = z.object({
  sheet: z.string(),
  /** Номера строк шапки, с 1. */
  headerRows: z.array(z.number().int().min(1)).min(1).max(2),
  columns: z.array(z.object({ letter: ColumnLetterSchema, label: z.string() })),
  rows: z.array(z.object({ row: z.number().int().min(1), cells: z.array(z.string()) })),
});
export type SheetGrid = z.infer<typeof SheetGridSchema>;

/** Колонка варианта: подпись, до 8 значений как в файле, роль и вероятности ролей (FR-008, FR-009). */
export const ColumnProposalSchema = z.object({
  letter: ColumnLetterSchema,
  label: z.string(),
  values: z.array(z.string()).max(8),
  role: ColumnRoleOrUnusedSchema,
  probabilities: z.partialRecord(ColumnRoleSchema, z.number().min(0).max(1)),
  by: DecidedBySchema,
});
export type ColumnProposal = z.infer<typeof ColumnProposalSchema>;

/** Поле схемы: колонка или «не найдено», уверенность и кто поставил (FR-011, FR-017). */
export const FieldProposalSchema = z.object({
  letter: ColumnLetterSchema.nullable(),
  confidence: z.number().min(0).max(1),
  by: DecidedBySchema,
});

/** Проверка содержимым (FR-013, FR-014). */
export const onboardingWarningKinds = ['due_without_dates', 'responsible_empty'] as const;
export const OnboardingWarningKindSchema = z.enum(onboardingWarningKinds);
export const OnboardingWarningSchema = z.object({
  kind: OnboardingWarningKindSchema,
  letter: ColumnLetterSchema,
  /** Колонки, где дат не меньше двух, — кандидаты в срок. */
  candidates: z.array(ColumnLetterSchema),
});
export type OnboardingWarning = z.infer<typeof OnboardingWarningSchema>;

/**
 * Вопрос администратору (FR-018): поле ниже порога, предупреждение, строка шапки или строка
 * листа с недоказанным родителем или ролью (ядро 1.7 и слой C2; модель о строении не спрашивают).
 */
export const OnboardingQuestionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('field'),
    field: TemplateFieldSchema,
    proposal: z
      .object({ letter: ColumnLetterSchema, probability: z.number().min(0).max(1) })
      .nullable(),
    candidates: z.array(ColumnLetterSchema),
  }),
  z.object({
    kind: z.literal('warning'),
    warning: OnboardingWarningKindSchema,
    field: TemplateFieldSchema,
    letter: ColumnLetterSchema,
    candidates: z.array(ColumnLetterSchema),
  }),
  z.object({
    kind: z.literal('header_row'),
    proposal: z
      .object({ row: z.number().int().min(1).max(10), probability: z.number().min(0).max(1) })
      .nullable(),
    candidates: z.array(z.number().int().min(1).max(10)),
  }),
  z.object({
    kind: z.literal('row'),
    row: z.number().int().min(1),
    /** Строки-кандидаты родителя; null — корень листа. */
    candidates: z.array(z.number().int().min(1).nullable()),
  }),
]);
export type OnboardingQuestion = z.infer<typeof OnboardingQuestionSchema>;

/** Вид строки в шаге «Строки»: проекция снимка ядра 1.7 и поправок. group — группа работ, пункт. */
export const rowViewKinds = ['section', 'subsection', 'group', 'item', 'subitem', 'other'] as const;
export const RowViewKindSchema = z.enum(rowViewKinds);
export type RowViewKind = (typeof rowViewKinds)[number];

export const RowViewSchema = z.object({
  row: z.number().int().min(1),
  kind: RowViewKindSchema,
  hidden: z.boolean(),
  parentRow: z.number().int().min(1).nullable(),
  parentState: DecisionStateSchema,
  candidates: z.array(z.number().int().min(1).nullable()),
  /** Физические строки предков сверху вниз; у неопределённого родителя — известная часть. */
  path: z.array(z.number().int().min(1)),
  pathState: HierarchyStateSchema,
  number: z.string().nullable(),
  name: z.string().nullable(),
  due: DateSchema.nullable(),
  responsible: z.string().nullable(),
  mark: z.string().nullable(),
  /** Основание ядра: «bold_centered_empty_right», «dash_list_without_formatted_parent»… */
  reason: z.string(),
  decision: z.object({ by: z.enum(['rule', 'admin']) }),
});
export type RowView = z.infer<typeof RowViewSchema>;

export const RowsViewSchema = z.object({
  rows: z.array(RowViewSchema),
  totals: z.object({
    items: z.number().int().min(0),
    subitems: z.number().int().min(0),
    groups: z.number().int().min(0),
    headers: z.number().int().min(0),
    noDue: z.number().int().min(0),
    noResponsible: z.number().int().min(0),
    questions: z.number().int().min(0),
  }),
  /** Строки с недоказанным родителем или ролью — вопросы шага «Строки». */
  questions: z.array(z.number().int().min(1)),
});
export type RowsView = z.infer<typeof RowsViewSchema>;

/** Отказ модели: код TPL-006, причина и поля, оставшиеся вопросами (FR-052). */
export const OnboardingRefusalSchema = z.object({
  code: z.literal('TPL-006'),
  refusal: z.string(),
  fields: z.array(TemplateFieldSchema),
});

export const OnboardingVariantSchema = z.object({
  /** Ключ варианта: подписи шапки. */
  key: z.string(),
  /** Листы варианта по порядку книги. */
  sheets: z.array(z.string()).min(1),
  sample: z.string(),
  /** Строка шапки, с 1, и число строк шапки. */
  headerRow: z.number().int().min(1),
  headerRows: HeaderRowsSchema,
  headerBy: DecidedBySchema,
  headerProbability: z.number().min(0).max(1).nullable(),
  /** Заголовок протокола над шапкой без даты — название схемы по умолчанию (FR-029). */
  title: z.string().nullable(),
  grid: SheetGridSchema,
  columns: z.array(ColumnProposalSchema),
  fields: z.partialRecord(TemplateFieldSchema, FieldProposalSchema),
  warnings: z.array(OnboardingWarningSchema),
  questions: z.array(OnboardingQuestionSchema),
  /** Действующая схема, с которой совпадает больше половины подписей (FR-021). */
  similar: z
    .object({
      templateId: IdSchema,
      title: z.string(),
      matched: z.number().int().min(0),
      total: z.number().int().min(1),
      columns: z.record(z.string().regex(/^\d+$/), ColumnAssignmentSchema),
    })
    .nullable(),
  /** Архивная схема с теми же подписями — мастер предлагает вернуть её (TPL-004). */
  archivedMatch: z.object({ templateId: IdSchema, title: z.string() }).nullable(),
  rows: RowsViewSchema,
  modelRefusal: OnboardingRefusalSchema.nullable(),
});
export type OnboardingVariant = z.infer<typeof OnboardingVariantSchema>;

export const OnboardingDraftSchema = z.object({
  /** Файл мастера в хранилище — будущий лист-образец (FR-033). */
  fileId: IdSchema,
  fileName: z.string(),
  sheetsTotal: z.number().int().min(0),
  skipped: z.object({
    archived: z.array(z.string()),
    empty: z.array(z.string()),
    /** Листы, узнанные действующей схемой: «Узнан схемой «…»». */
    known: z.array(z.object({ sheet: z.string(), templateId: IdSchema, title: z.string() })),
  }),
  variants: z.array(OnboardingVariantSchema),
});
export type OnboardingDraft = z.infer<typeof OnboardingDraftSchema>;

/** Поля схемы → буквы колонок черновой карты. */
export const FieldColumnsSchema = z.partialRecord(TemplateFieldSchema, ColumnLetterSchema);

/** Пересчёт строк варианта по черновой карте и поправкам (templates.onboarding.rows). */
export const OnboardingRowsBodySchema = z.object({
  fileId: IdSchema,
  sheet: z.string().min(1),
  headerRow: z.number().int().min(1).max(10),
  headerRows: HeaderRowsSchema,
  columns: FieldColumnsSchema,
  rowKinds: RowKindCorrectionsSchema,
});
export type OnboardingRowsBody = z.infer<typeof OnboardingRowsBodySchema>;

/** Подтверждение варианта (templates.create): схема с версией 1 (FR-029). */
export const CreateTemplateBodySchema = z.object({
  fileId: IdSchema,
  sheet: z.string().min(1),
  headerRow: z.number().int().min(1).max(10),
  headerRows: HeaderRowsSchema,
  title: z.string().trim().min(1).max(120),
  columns: FieldColumnsSchema,
  /** Карта ролей всех колонок варианта по буквам. */
  roles: z.record(ColumnLetterSchema, ColumnAssignmentSchema),
  rowKinds: RowKindCorrectionsSchema,
  /** Сколько полей и поправок поставил ИИ и сколько — администратор. */
  answers: z.object({ ai: z.number().int().min(0), admin: z.number().int().min(0) }),
});
export type CreateTemplateBody = z.infer<typeof CreateTemplateBodySchema>;
